import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintWithGlyph, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import {
  firstOfThisMonth,
  formatMonth,
  formatNumber,
  MRP_LOCATIONS,
  mrpLocationChipStyles,
  type MrpBuildScenarioShortageRow,
  type MrpGhostBuildOrderDraftLine,
  type MrpGhostBuildOrderRow,
  type MrpLocation,
} from "@/lib/mrp";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";

const HELP_PAGE =
  "Enter finished-good SKUs, locations, plan months, and build quantities to preview incremental component shortages on top of the live forecast. Pin lines as ghost build orders to add what-if demand to Forecast without changing the real build plan.";

function newDraftLine(planMonth: string): MrpGhostBuildOrderDraftLine {
  return {
    key: crypto.randomUUID(),
    sku: "",
    location_code: "sgs",
    plan_month: planMonth,
    quantity: "",
    notes: "",
  };
}

function parsePositiveQuantity(raw: string): number | null {
  const parsed = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function GhostBuildOrders() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const defaultPlanMonth = firstOfThisMonth();
  const [draftLines, setDraftLines] = useState<MrpGhostBuildOrderDraftLine[]>(() => [newDraftLine(defaultPlanMonth)]);
  const [previewRows, setPreviewRows] = useState<MrpBuildScenarioShortageRow[]>([]);
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);

  const pinnedQuery = useQuery({
    queryKey: ["mrp", "ghost-build-orders"],
    queryFn: () => apiFetch<{ rows: MrpGhostBuildOrderRow[] }>("/api/mrp/ghost-build-orders"),
    staleTime: 30_000,
  });

  const previewMutation = useMutation({
    mutationFn: async (lines: MrpGhostBuildOrderDraftLine[]) => {
      const payloadLines = [];
      for (const line of lines) {
        const quantity = parsePositiveQuantity(line.quantity);
        if (!line.sku.trim() || quantity == null) continue;
        payloadLines.push({
          sku: line.sku.trim(),
          location_code: line.location_code,
          plan_month: line.plan_month,
          quantity,
        });
      }
      return apiFetch<{
        rows: MrpBuildScenarioShortageRow[];
        warnings: { message?: string; finished_good_sku?: string; assembly_code?: string }[];
      }>("/api/mrp/ghost-build-orders/preview", {
        method: "POST",
        body: { lines: payloadLines },
      });
    },
    onSuccess: (data) => {
      setPreviewRows(data.rows);
      setPreviewWarnings(
        (data.warnings ?? [])
          .map((warning) => warning.message ?? warning.finished_good_sku ?? warning.assembly_code ?? "")
          .filter((message) => message.trim() !== ""),
      );
    },
  });

  const pinMutation = useMutation({
    mutationFn: async (lines: MrpGhostBuildOrderDraftLine[]) => {
      const payloadLines = [];
      for (const line of lines) {
        const quantity = parsePositiveQuantity(line.quantity);
        if (!line.sku.trim() || quantity == null) {
          throw new Error("Each line needs a SKU and a positive quantity before pinning.");
        }
        payloadLines.push({
          sku: line.sku.trim(),
          location_code: line.location_code,
          plan_month: line.plan_month,
          quantity,
          notes: line.notes.trim() || null,
        });
      }
      if (payloadLines.length === 0) {
        throw new Error("Add at least one complete line before pinning.");
      }
      return apiFetch<{ rows: MrpGhostBuildOrderRow[] }>("/api/mrp/ghost-build-orders", {
        method: "PUT",
        body: { lines: payloadLines },
      });
    },
    onSuccess: () => {
      setDraftLines([newDraftLine(defaultPlanMonth)]);
      setPreviewRows([]);
      setPreviewWarnings([]);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "ghost-build-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
    },
  });

  const deletePinnedMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/mrp/ghost-build-orders/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "ghost-build-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
    },
  });

  const clearPinnedMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ deleted_count: number }>("/api/mrp/ghost-build-orders", {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "ghost-build-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
    },
  });

  const updateDraftLine = useCallback((key: string, patch: Partial<MrpGhostBuildOrderDraftLine>) => {
    setDraftLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  }, []);

  const removeDraftLine = useCallback((key: string) => {
    setDraftLines((current) => (current.length <= 1 ? current : current.filter((line) => line.key !== key)));
  }, []);

  const pinnedRows = pinnedQuery.data?.rows ?? [];
  const previewDisabled = previewMutation.isPending || draftLines.every((line) => !line.sku.trim());

  const draftSummary = useMemo(() => {
    const complete = draftLines.filter((line) => line.sku.trim() && parsePositiveQuantity(line.quantity) != null);
    return `${complete.length} of ${draftLines.length} draft ${draftLines.length === 1 ? "line" : "lines"} ready`;
  }, [draftLines]);

  const pinnedColumns = useMemo((): HubDataTableColumn<MrpGhostBuildOrderRow, string>[] => {
    return [
      {
        id: "sku",
        header: "Finished-good SKU",
        sortKey: "sku",
        sortValue: (row) => row.sku,
        cell: (row) => (
          <span className="font-mono text-xs">
            <MrpSkuDetailLink sku={row.sku}>{row.sku}</MrpSkuDetailLink>
          </span>
        ),
      },
      {
        id: "location",
        header: "Location",
        sortKey: "location",
        sortValue: (row) => row.location_code,
        cell: (row) => {
          const chip = mrpLocationChipStyles(row.location_code);
          return (
            <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-xs", chip.className)}>
              {chip.label}
            </span>
          );
        },
      },
      {
        id: "plan_month",
        header: "Plan month",
        sortKey: "plan_month",
        sortValue: (row) => row.plan_month,
        cell: (row) => formatMonth(row.plan_month),
      },
      {
        id: "quantity",
        header: "Quantity",
        sortKey: "quantity",
        align: "right",
        sortValue: (row) => row.quantity,
        cell: (row) => formatNumber(row.quantity),
      },
      {
        id: "notes",
        header: "Notes",
        sortKey: "notes",
        sortValue: (row) => row.notes ?? "",
        cell: (row) => <span className="text-slate-600">{row.notes ?? ""}</span>,
      },
      {
        id: "actions",
        header: "",
        sortKey: "actions",
        align: "right",
        sortValue: () => "",
        cell: (row) => (
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={!hubWritesAllowed || deletePinnedMutation.isPending}
              onClick={() => {
                if (!hubWritesAllowed) return;
                deletePinnedMutation.mutate(row.id);
              }}
            >
              Remove
            </Button>
          </ReadOnlyHoverLayer>
        ),
      },
    ];
  }, [
    deletePinnedMutation,
    hubWritesAllowed,
    mrpReadOnlyTitle,
  ]);

  const previewColumns = useMemo((): HubDataTableColumn<MrpBuildScenarioShortageRow, string>[] => {
    return [
      {
        id: "cpn",
        header: "Component",
        sortKey: "cpn",
        sortValue: (row) => row.cpn,
        cell: (row) => (
          <>
            <div className="font-mono text-xs">
              <MrpSkuDetailLink sku={row.cpn}>{row.cpn}</MrpSkuDetailLink>
            </div>
            {row.display_name ? <div className="mt-0.5 text-xs text-slate-500">{row.display_name}</div> : null}
          </>
        ),
      },
      {
        id: "location",
        header: "Location",
        sortKey: "location",
        sortValue: (row) => row.location_code,
        cell: (row) => {
          const chip = mrpLocationChipStyles(row.location_code);
          return (
            <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-xs", chip.className)}>
              {chip.label}
            </span>
          );
        },
      },
      {
        id: "month",
        header: "Month",
        sortKey: "month",
        sortValue: (row) => row.month,
        cell: (row) => formatMonth(row.month),
      },
      {
        id: "baseline_shortfall",
        header: "Baseline shortfall",
        sortKey: "baseline_shortfall",
        align: "right",
        sortValue: (row) => row.baseline_shortfall,
        cell: (row) => formatNumber(row.baseline_shortfall),
      },
      {
        id: "scenario_shortfall",
        header: "Scenario shortfall",
        sortKey: "scenario_shortfall",
        align: "right",
        sortValue: (row) => row.scenario_shortfall,
        cell: (row) => formatNumber(row.scenario_shortfall),
      },
      {
        id: "incremental_shortfall",
        header: "Incremental",
        sortKey: "incremental_shortfall",
        align: "right",
        sortValue: (row) => row.incremental_shortfall,
        cell: (row) => (
          <span className="font-medium text-rose-700">{formatNumber(row.incremental_shortfall)}</span>
        ),
      },
      {
        id: "scenario_order_qty",
        header: "Suggested order",
        sortKey: "scenario_order_qty",
        align: "right",
        sortValue: (row) => row.scenario_order_qty,
        cell: (row) => formatNumber(row.scenario_order_qty),
      },
      {
        id: "build_by",
        header: "Build by SKU",
        sortKey: "build_by",
        sortValue: (row) =>
          row.consuming_skus.map((entry) => `${entry.sku} (${formatNumber(entry.quantity)})`).join(", "),
        cell: (row) => {
          const buildBy = row.consuming_skus
            .map((entry) => `${entry.sku} (${formatNumber(entry.quantity)})`)
            .join(", ");
          return <span className="text-xs text-slate-600">{buildBy || "—"}</span>;
        },
      },
    ];
  }, []);

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={HELP_PAGE}>Ghost build orders</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Pinned ghost builds</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">
            Pinned rows add finished-good demand to the live{" "}
            <Link to="/mrp/forecast" className="text-brand-700 hover:underline">
              Forecast
            </Link>{" "}
            without changing the build plan.
          </p>
          {pinnedQuery.isLoading ? <p className="text-sm text-slate-500">Loading pinned rows…</p> : null}
          {pinnedQuery.error ? (
            <p className="text-sm text-rose-700">{(pinnedQuery.error as Error).message}</p>
          ) : null}
          {pinnedRows.length > 0 ? (
            <HubDataTable
              fillViewport={false}
              className="max-h-none border-0 shadow-none"
              columns={pinnedColumns}
              rows={pinnedRows}
              rowKey={(row) => row.id}
              loading={pinnedQuery.isLoading}
              empty="No pinned ghost build orders yet."
            />
          ) : pinnedQuery.isSuccess ? (
            <p className="text-sm text-slate-500">No pinned ghost build orders yet.</p>
          ) : null}
          {pinnedRows.length > 0 ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
              <Button
                type="button"
                size="sm"
                variant="danger"
                disabled={!hubWritesAllowed || clearPinnedMutation.isPending}
                isLoading={clearPinnedMutation.isPending}
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  if (
                    !window.confirm(
                      "Delete all pinned ghost build orders? Forecast demand from these rows will be removed.",
                    )
                  ) {
                    return;
                  }
                  clearPinnedMutation.mutate();
                }}
              >
                Clear all pinned
              </Button>
            </ReadOnlyHoverLayer>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Draft build lines</CardTitle>
            <HoverHintWithGlyph hoverHint="Preview uses only these draft lines on top of the live forecast, including any pinned ghost builds already saved." />
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600">{draftSummary}</p>
          <div className="space-y-3">
            {draftLines.map((line, index) => (
              <div
                key={line.key}
                className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 lg:grid-cols-[minmax(12rem,1.4fr)_minmax(8rem,0.7fr)_minmax(9rem,0.8fr)_minmax(6rem,0.5fr)_minmax(10rem,1fr)_auto]"
              >
                <div>
                  <Label className="mb-1 block text-xs">Finished-good SKU</Label>
                  <AssemblySkuCombobox
                    enforceCatalogSku
                    value={line.sku}
                    onChange={(sku) => updateDraftLine(line.key, { sku })}
                    onBlurCommittedWithSku={(sku) => updateDraftLine(line.key, { sku })}
                    placeholder={`Search SKU for line ${index + 1}…`}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor={`ghost-location-${line.key}`} className="mb-1 block text-xs">
                    Location
                  </Label>
                  <select
                    id={`ghost-location-${line.key}`}
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={line.location_code}
                    onChange={(event) =>
                      updateDraftLine(line.key, { location_code: event.target.value as MrpLocation })
                    }
                  >
                    {MRP_LOCATIONS.map((location) => (
                      <option key={location.code} value={location.code}>
                        {location.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor={`ghost-month-${line.key}`} className="mb-1 block text-xs">
                    Plan month
                  </Label>
                  <Input
                    id={`ghost-month-${line.key}`}
                    type="month"
                    value={line.plan_month.slice(0, 7)}
                    onChange={(event) => {
                      const value = event.target.value;
                      updateDraftLine(line.key, {
                        plan_month: value ? `${value}-01` : defaultPlanMonth,
                      });
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor={`ghost-qty-${line.key}`} className="mb-1 block text-xs">
                    Quantity
                  </Label>
                  <Input
                    id={`ghost-qty-${line.key}`}
                    type="text"
                    inputMode="numeric"
                    value={line.quantity}
                    onChange={(event) => updateDraftLine(line.key, { quantity: event.target.value })}
                    placeholder="Build qty"
                  />
                </div>
                <div>
                  <Label htmlFor={`ghost-notes-${line.key}`} className="mb-1 block text-xs">
                    Notes
                  </Label>
                  <Input
                    id={`ghost-notes-${line.key}`}
                    value={line.notes}
                    onChange={(event) => updateDraftLine(line.key, { notes: event.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={draftLines.length <= 1}
                    onClick={() => removeDraftLine(line.key)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setDraftLines((current) => [...current, newDraftLine(defaultPlanMonth)])}>
              Add line
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={previewDisabled}
              isLoading={previewMutation.isPending}
              onClick={() => previewMutation.mutate(draftLines)}
            >
              Preview shortages
            </Button>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
              <Button
                type="button"
                size="sm"
                disabled={!hubWritesAllowed || pinMutation.isPending}
                isLoading={pinMutation.isPending}
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  pinMutation.mutate(draftLines);
                }}
              >
                Pin draft lines
              </Button>
            </ReadOnlyHoverLayer>
          </div>
          {previewMutation.error ? (
            <p className="text-sm text-rose-700">{(previewMutation.error as Error).message}</p>
          ) : null}
          {pinMutation.error ? <p className="text-sm text-rose-700">{(pinMutation.error as Error).message}</p> : null}
        </CardBody>
      </Card>

      {(previewRows.length > 0 || previewWarnings.length > 0) && !previewMutation.isPending ? (
        <Card>
          <CardHeader>
            <CardTitle>Incremental shortages</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            {previewWarnings.length > 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {previewWarnings.map((warning) => (
                  <p key={warning}>{warning}</p>
                ))}
              </div>
            ) : null}
            {previewRows.length === 0 ? (
              <p className="text-sm text-slate-600">No incremental component shortages from these draft lines.</p>
            ) : (
              <HubDataTable
                fillViewport={false}
                className="max-h-none border-0 shadow-none"
                columns={previewColumns}
                rows={previewRows}
                rowKey={(row) => `${row.cpn}|${row.location_code}|${row.month}`}
                empty="No incremental component shortages from these draft lines."
              />
            )}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
